import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import CircularProgress from "@mui/material/CircularProgress";
import SearchIcon from "@mui/icons-material/Search";
import { colorPalette } from "../styles/theme";
import { wsApi } from "../api/services";
import SurveyStatsView from "./SurveyStatsView";
import SatisfactionSurveyDetailPrint from "../components/pdf/SatisfactionSurveyDetailPrint";

/**
 * 만족도조사 통계 탭 노출 여부
 * - 월성복지관: 소속 관리자든 마스터든 항상 노출
 * - 가람마인드: 마스터 관리자(level === 0)가 조회할 때만 노출
 */
const shouldShowSatisfactionTab = (organizationName, isMasterAdmin) => {
  if (organizationName === "월성복지관") return true;
  if (organizationName === "가람마인드" && isMasterAdmin) return true;
  return false;
};

/**
 * 소속/사업별 참가자 관리 화면 (실 API 연동)
 * - 사이드바에서 선택한 프로젝트(사업)의 code로 GET /users?code= 를 호출해 참가자 목록을 조회합니다.
 * - 참가자를 선택하면 POST /users/program-detail 로 PHQ-9/GAD-7 회차 이력과 주간 지표를 조회합니다.
 */

const PAGE_SIZE = 10;
const DISPLAY_PAGE_COUNT = 5; // 페이지네이션 번호 그룹당 표시 개수

const genderLabel = (gender) => {
  if (gender === "M") return "남";
  if (gender === "F") return "여";
  return gender || "-";
};

const ageLabel = (age) => (typeof age === "number" ? `${age}세` : "- 세");

// app_usage(활발/잠잠 등) 배지 색상 — 위험 배지와 동일한 스타일(원형 아님, solid Chip)
const APP_USAGE_STYLE = {
  활발: { backgroundColor: "#22c55e", color: "#fff" },
  잠잠: { backgroundColor: "#9ca3af", color: "#fff" },
};

const getAppUsageStyle = (usage) =>
  APP_USAGE_STYLE[usage] ?? { backgroundColor: "#e5e7eb", color: colorPalette.textSecondary };

const PERIOD_LABEL_BY_LETTER = { S: "단기", M: "중기", L: "장기" };

/**
 * registered_invite_code에서 반(class) 정보를 추출합니다.
 * 예: 프로젝트 code가 "WS-YOUTH-2026"이고 invite code가 "WS-YOUTH-2026-M1-1"이면
 * 앞의 프로젝트 code(초대코드)를 떼어낸 "M1-1"에서
 * - 맨 앞 알파벳(S/M/L) → 단기/중기/장기
 * - 맨 뒤 숫자 → 반 번호(1반/2반/3반)
 * 로 해석합니다. 형식이 다른(다른 사업의) invite code는 null을 반환합니다.
 */
const parseClassInfo = (inviteCode, projectCode) => {
  if (!inviteCode) return null;

  let remainder = inviteCode;
  if (projectCode && remainder.toUpperCase().startsWith(`${projectCode.toUpperCase()}-`)) {
    remainder = remainder.slice(projectCode.length + 1);
  }

  const match = remainder.match(/^([SML])\d*-?(\d+)$/i);
  if (!match) return null;

  const periodLetter = match[1].toUpperCase();
  const classNumber = Number(match[2]);
  const periodLabel = PERIOD_LABEL_BY_LETTER[periodLetter] ?? periodLetter;

  return {
    periodLetter,
    periodLabel,
    classNumber,
    label: `${periodLabel} ${classNumber}반`,
  };
};

const getViewConfig = (project) => {
  if (!project) {
    return {
      title: "참가자 관리",
      description: "좌측 사이드바에서 소속/사업을 선택하면 참가자 목록이 표시됩니다.",
    };
  }

  return {
    title: `${project.name} 참가자 관리`,
    description: project.organizationName
      ? `${project.organizationName} · ${project.name} 소속 참가자 현황입니다.`
      : `${project.name} 소속 참가자 현황입니다.`,
  };
};

// PsychologicalTestStatus.js와 동일한 기준의 점수 구간별 색상
const getScoreColor = (type, score) => {
  if (typeof score !== "number") return "#e5e7eb";
  if (type === "phq") {
    if (score >= 20) return "#ff3b30";
    if (score >= 15) return "#f6788b";
    if (score >= 10) return "#ffa24d";
    if (score >= 5) return "#ffd561";
    return "#929292";
  }
  if (score >= 15) return "#ff3b30";
  if (score >= 10) return "#ffa24d";
  if (score >= 5) return "#ffd561";
  return "#929292";
};

// 일일 디스트레스 점수(0~1) 4단계 위험 상태 분류 (대응 PHQ-9/GAD-7 밴드 기준표와 동일)
const DISTRESS_LEVELS = [
  { min: 0, max: 0.16, label: "정상", color: "#22c55e" },
  { min: 0.17, max: 0.41, label: "경미/중등도 위험", color: "#facc15" },
  { min: 0.42, max: 0.6, label: "중등도/심각 위험", color: "#f97316" },
  { min: 0.61, max: 1, label: "중증 위험", color: "#ef4444" },
];

const getDistressLevel = (value) => {
  if (typeof value !== "number") return null;
  return DISTRESS_LEVELS.find((level) => value >= level.min && value <= level.max) ?? null;
};

// PHQ-9 / GAD-7 / 한국판 정신적 웰빙 척도(14문항 × 0~5점) 총점 범위 (그래프 y축 상한 고정에 사용)
const ASSESSMENT_MAX_SCORE = { phq: 27, gad: 21, wellbeing: 70 };

// 한국판 정신적 웰빙 척도를 노출하는 사업 code (대구한의대학교 - 서버도 이 code일 때만 wellbeing 데이터를 응답)
const WELLBEING_PROJECT_CODE = "GROW2026";

/** stats API의 추이 배열({ date, average_score/symptom_score/..., count/episode_count })을 TrendLineChart용 { date, avg, count }로 변환 */
const normalizeTrend = (items, valueKey, countKey) =>
  (Array.isArray(items) ? items : []).map((d) => ({
    date: d.date,
    avg: Number(d[valueKey] ?? 0),
    count: d[countKey] ?? 0,
  }));

/** 검사가 없었던 날짜도 x축에 표시되도록, 실제 데이터의 최소~최대 날짜 사이 빈 날짜를 avg:null로 채움 */
const fillTrendDateGaps = (items) => {
  if (!items || items.length === 0) return [];

  const sorted = [...items].sort((a, b) => (a.date < b.date ? -1 : 1));
  const byDate = new Map(sorted.map((d) => [d.date, d]));

  const filled = [];
  const cursor = new Date(sorted[0].date);
  const end = new Date(sorted[sorted.length - 1].date);
  while (cursor <= end) {
    const dateStr = cursor.toISOString().slice(0, 10);
    filled.push(byDate.get(dateStr) ?? { date: dateStr, avg: null, count: 0 });
    cursor.setDate(cursor.getDate() + 1);
  }
  return filled;
};

/** DailyDualLineChart의 y축 상한 계산(데이터 최댓값 * 1.25, 최소 0.1)과 동일한 방식 — referenceValue(지역 평균)도 스케일에 포함 */
const computeDynamicMaxY = (trend, referenceValue) => {
  const values = trend.map((d) => d.avg).filter((v) => typeof v === "number");
  if (typeof referenceValue === "number") values.push(referenceValue);
  const maxValue = values.length ? Math.max(...values) : 1;
  return Math.max(maxValue * 1.25, 0.1);
};

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value).slice(0, 10);
  return parsed.toLocaleDateString("sv-SE");
};

const formatCellValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "예" : "아니오";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const TabPanel = ({ value, index, children }) => {
  if (value !== index) return null;
  return <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>{children}</Box>;
};

/**
 * 참가자에게 검사 알림을 발송하는 버튼 (POST /users/survey-notification → 앱 검사 요청 팝업)
 * @param {string} type - survey_type ("phq" | "gad" | "wellbeing")
 */
const SurveyRequestButton = ({ label, type, userId }) => {
  const [requesting, setRequesting] = useState(false);
  const [requested, setRequested] = useState(false);

  const handleRequest = async () => {
    if (!userId) {
      alert("사용자 정보를 찾을 수 없습니다.");
      return;
    }
    if (!window.confirm(`${label} 요청을 발송하시겠습니까?`)) return;

    try {
      setRequesting(true);
      const res = await wsApi.sendSurveyNotification(userId, type);
      setRequested(true);
      alert(res.data?.message || `${label} 요청이 정상적으로 발송되었습니다.`);
    } catch (err) {
      alert(`검사 요청 발송에 실패했습니다: ${err.response?.data?.message || err.message}`);
    } finally {
      setRequesting(false);
    }
  };

  return (
    <Button
      size="small"
      variant="outlined"
      disabled={requesting || requested}
      onClick={handleRequest}
      sx={{ minWidth: 64, height: 24, px: 1, fontSize: 11, fontWeight: 700 }}
    >
      {requesting ? "발송 중..." : requested ? "요청됨" : "검사 요청"}
    </Button>
  );
};

/** 선택된 참가자의 PHQ-9 / GAD-7 회차별 이력 (program-detail API 응답) */
const HistoryList = ({ label, type, history = [], userId }) => {
  return (
  <Box sx={{ flex: 1, minWidth: 260 }}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, color: colorPalette.textSecondary }}
      >
        {label} ({history.length}회)
      </Typography>
      <SurveyRequestButton label={label} type={type} userId={userId} />
    </Box>
    {history.length === 0 ? (
      <Box
        sx={{
          py: 3,
          textAlign: "center",
          border: `1px dashed ${colorPalette.border}`,
          borderRadius: 1,
          color: colorPalette.textSecondary,
        }}
      >
        <Typography variant="caption">검사 이력이 없습니다.</Typography>
      </Box>
    ) : (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {history.map((item, idx) => (
          <Box
            key={item.attempt ?? item.completed_at ?? idx}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              px: 1.5,
              py: 1,
              borderRadius: 1,
              border: `1px solid ${colorPalette.border}`,
              backgroundColor: colorPalette.white,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: colorPalette.primary }}>
                {item.attempt}차
              </Typography>
              <Typography variant="caption" sx={{ color: colorPalette.textSecondary }}>
                {formatDate(item.completed_at)}
              </Typography>
              {item.severity_level && (
                <Chip
                  label={item.severity_level}
                  size="small"
                  sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
                />
              )}
            </Box>
            <Chip
              label={`${item.total_score}점`}
              size="small"
              sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 700,
                backgroundColor: getScoreColor(type, item.total_score),
                color: "#111827",
              }}
            />
          </Box>
        ))}
      </Box>
    )}
  </Box>
  );
};

/** 월성복지관(ws-detail) 응답의 만족도 조사 이력 (satisfaction_surveys: [{ id, is_completed, completed_at }]) */
const SatisfactionSurveyList = ({ surveys = [], userId, participantName, participantLabel }) => {
  const [printingId, setPrintingId] = useState(null);
  const [printTarget, setPrintTarget] = useState(null); // { detail, roundLabel }

  // 인쇄 레이아웃이 렌더링된 뒤 인쇄 다이얼로그를 띄우고, 닫히면 레이아웃 제거
  // PDF 저장 파일명은 document.title 을 따르므로 인쇄 중에만 "[이름]_만족도조사" 로 변경
  useEffect(() => {
    if (!printTarget) return undefined;
    const originalTitle = document.title;
    document.title = `${participantName || "참가자"}_만족도조사`;
    const handleAfterPrint = () => setPrintTarget(null);
    window.addEventListener("afterprint", handleAfterPrint);
    const timer = setTimeout(() => window.print(), 0);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("afterprint", handleAfterPrint);
      document.title = originalTitle;
    };
  }, [printTarget, participantName]);

  const handlePrint = async (item, roundLabel) => {
    if (!userId || !item?.id) return;
    setPrintingId(item.id);
    try {
      const res = await wsApi.getUserSatisfactionSurveyDetail(userId, item.id);
      setPrintTarget({ detail: res.data, roundLabel });
    } catch (err) {
      // console.error("[Error] 만족도 조사 상세 조회 실패:", err);
      alert(err.response?.data?.message || "만족도 조사 결과를 불러오지 못했습니다.");
    } finally {
      setPrintingId(null);
    }
  };

  return (
  <Box sx={{ flex: 1, minWidth: 260 }}>
    <Typography
      variant="caption"
      sx={{ display: "block", mb: 1, fontWeight: 700, color: colorPalette.textSecondary }}
    >
      만족도 조사 ({surveys.length}회)
    </Typography>
    {surveys.length === 0 ? (
      <Box
        sx={{
          py: 3,
          textAlign: "center",
          border: `1px dashed ${colorPalette.border}`,
          borderRadius: 1,
          color: colorPalette.textSecondary,
        }}
      >
        <Typography variant="caption">조사 이력이 없습니다.</Typography>
      </Box>
    ) : (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {surveys.map((item, idx) => {
          const roundLabel = `${surveys.length - idx}차`;
          return (
          <Box
            key={item.id ?? idx}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              px: 1.5,
              py: 1,
              borderRadius: 1,
              border: `1px solid ${colorPalette.border}`,
              backgroundColor: colorPalette.white,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: colorPalette.primary }}>
                {roundLabel}
              </Typography>
              <Typography variant="caption" sx={{ color: colorPalette.textSecondary }}>
                {formatDate(item.completed_at)}
              </Typography>
              <Chip
                label={item.is_completed ? "완료" : "진행중"}
                size="small"
                sx={{
                  height: 20,
                  fontSize: 10,
                  fontWeight: 700,
                  backgroundColor: item.is_completed ? "#c7e8ff" : "#cfe8bf",
                  color: "#111827",
                }}
              />
            </Box>
            <Button
              variant="outlined"
              size="small"
              disabled={!item.is_completed || printingId !== null}
              onClick={() => handlePrint(item, roundLabel)}
              sx={{ minWidth: 72, height: 26, fontSize: 11, fontWeight: 700 }}
            >
              {printingId === item.id ? <CircularProgress size={14} /> : "PDF 출력"}
            </Button>
          </Box>
          );
        })}
      </Box>
    )}
    <SatisfactionSurveyDetailPrint
      detail={printTarget?.detail}
      roundLabel={printTarget?.roundLabel}
      participantLabel={participantLabel}
    />
  </Box>
  );
};

/**
 * 하위 척도 점수가 있는 검사 이력 (총점 + sub_scores)
 * - 월성복지관(ws-detail) 취업준비도(K-EHS): employment_readiness [{ id, is_completed, completed_at, total_score, sub_scores }]
 * - GROW2026(program-detail) 한국판 정신적 웰빙 척도: wellbeing_history [{ attempt, completed_at, total_score, sub_scores }] (완료 건만 응답)
 * surveyType/userId를 넘기면 제목 옆에 검사 요청 버튼을 표시
 */
const SubScoreHistoryList = ({ title, items = [], surveyType, userId }) => (
  <Box sx={{ mb: 4 }}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colorPalette.textPrimary }}>
        {title} ({items.length}회)
      </Typography>
      {surveyType && <SurveyRequestButton label={title} type={surveyType} userId={userId} />}
    </Box>
    {items.length === 0 ? (
      <Box
        sx={{
          py: 3,
          textAlign: "center",
          border: `1px dashed ${colorPalette.border}`,
          borderRadius: 1,
          color: colorPalette.textSecondary,
        }}
      >
        <Typography variant="caption">조사 이력이 없습니다.</Typography>
      </Box>
    ) : (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {items.map((item, idx) => {
          const isCompleted = item.is_completed ?? true;
          return (
          <Paper key={item.id ?? item.attempt ?? idx} variant="outlined" sx={{ p: 2, backgroundColor: colorPalette.white }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: isCompleted ? 1.5 : 0,
              }}
            >
              <Typography variant="caption" sx={{ color: colorPalette.textSecondary, fontWeight: 600 }}>
                {item.attempt ?? items.length - idx}차 · {formatDate(item.completed_at)}
              </Typography>
              {isCompleted ? (
                <Typography variant="subtitle2" sx={{ color: colorPalette.primary, fontWeight: 800 }}>
                  총점 {item.total_score}점
                </Typography>
              ) : (
                <Chip
                  label="진행중"
                  size="small"
                  sx={{ height: 20, fontSize: 10, fontWeight: 700, backgroundColor: "#cfe8bf", color: "#111827" }}
                />
              )}
            </Box>
            {isCompleted && Array.isArray(item.sub_scores) && item.sub_scores.length > 0 && (
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 1 }}>
                {item.sub_scores.map((sub, subIdx) => (
                  <Box
                    key={sub.title ?? sub.name ?? subIdx}
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderRadius: 1,
                      backgroundColor: colorPalette.background,
                    }}
                  >
                    <Typography variant="caption" sx={{ display: "block", color: colorPalette.textSecondary }}>
                      {sub.title ?? sub.name ?? `항목 ${subIdx + 1}`}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: colorPalette.textPrimary }}>
                      {sub.score ?? sub.value ?? "-"}점
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
          );
        })}
      </Box>
    )}
  </Box>
);

/** 임의의 key-value 형태 요약 객체를 그대로 렌더링 (필드명이 확정되지 않은 응답에도 대응) */
const KeyValueSummary = ({ data }) => {
  const entries = data && typeof data === "object" ? Object.entries(data) : [];
  if (entries.length === 0) {
    return (
      <Box
        sx={{
          py: 3,
          textAlign: "center",
          border: `1px dashed ${colorPalette.border}`,
          borderRadius: 1,
          color: colorPalette.textSecondary,
        }}
      >
        <Typography variant="caption">표시할 요약 데이터가 없습니다.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 1.5 }}>
      {entries.map(([key, value]) => (
        <Paper key={key} variant="outlined" sx={{ p: 1.5, backgroundColor: colorPalette.white }}>
          <Typography
            variant="caption"
            sx={{ display: "block", color: colorPalette.textSecondary, fontWeight: 600 }}
          >
            {key}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: colorPalette.textPrimary }}>
            {formatCellValue(value)}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
};

/** program-detail의 daily_scores를 개인 vs 기관 평균 이중선 그래프용 데이터로 변환 */
const buildDailyDualSeries = (dailyScores, userKey, orgKey) =>
  (Array.isArray(dailyScores) ? dailyScores : []).map((d) => ({
    date: d.date,
    userValue: typeof d[userKey] === "number" ? d[userKey] : null,
    orgValue: typeof d[orgKey] === "number" ? d[orgKey] : null,
    isEpisode: Boolean(d.is_episode),
  }));

/** 좌표 배열을 SVG path로 변환 — 값이 없는(null) 날짜는 건너뛰고, 유효한 점들끼리는 계속 이어서 그림 */
const buildLinePath = (points) => {
  let path = "";
  let started = false;
  points.forEach((p) => {
    if (!p) return;
    path += `${started ? "L" : "M"} ${p.x} ${p.y} `;
    started = true;
  });
  return path.trim();
};

/**
 * 개인 vs 기관 평균 이중선 그래프 (program-detail의 daily_scores 기반, 순수 SVG)
 * - 실선: 개인 점수 / 점선: 기관 평균 점수
 * - 빨간 테두리: 개인에게 그날 에피소드(is_episode)가 발생했음을 표시
 */
// 날짜 라벨이 겹치지 않도록 데이터 포인트 하나당 확보할 최소 가로 간격(px)
const MIN_X_STEP = 42;
const MIN_CHART_WIDTH = 560;

// 지역 평균 — 주간 단위로 바뀌는 기관 평균과 달리 항상 고정되는 기준값.
// 서버가 아직 이 값을 내려주지 않아 우선 하드코딩. API가 생기면 programDetail 응답 값으로 교체.
const REGION_AVERAGE = { symptomAvg: 0.64, distressAvg: 0.38 };

const DailyDualLineChart = ({ data, color, levels, regionAvg, regionLabel = "지역 평균" }) => {
  const height = 220;
  const padding = { top: 16, right: 16, bottom: 32, left: 36 };
  const [hoveredIndex, setHoveredIndex] = useState(null);

  if (!data || data.length === 0) {
    return (
      <Box
        sx={{
          py: 5,
          textAlign: "center",
          border: `1px dashed ${colorPalette.border}`,
          borderRadius: 1,
          color: colorPalette.textSecondary,
        }}
      >
        <Typography variant="body2">표시할 데이터가 없습니다.</Typography>
      </Box>
    );
  }

  // 데이터가 많아질수록(기간이 길수록) SVG 전체 너비를 늘려서 x축 라벨이 겹치지 않게 하고,
  // 바깥 Box의 overflowX:auto로 가로 스크롤이 생기도록 함
  const width = Math.max(MIN_CHART_WIDTH, padding.left + padding.right + (data.length - 1) * MIN_X_STEP);
  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;

  // levels(위험 단계 구간)가 주어지면 그 최대값(예: 1.0)에 y축을 고정해 구간 밴드가 항상 같은 비율로 보이게 함
  const allValues = data.flatMap((d) => [d.userValue, d.orgValue]).filter((v) => typeof v === "number");
  if (typeof regionAvg === "number") allValues.push(regionAvg);
  const maxValue = allValues.length ? Math.max(...allValues) : 1;
  const yMax = levels ? levels[levels.length - 1].max : Math.max(maxValue * 1.25, 0.1);

  const xStep = data.length > 1 ? innerWidth / (data.length - 1) : 0;
  const xScale = (index) => padding.left + xStep * index;
  const yScale = (value) => padding.top + innerHeight - (value / yMax) * innerHeight;

  const userPoints = data.map((d, i) => (typeof d.userValue === "number" ? { x: xScale(i), y: yScale(d.userValue) } : null));
  const orgPoints = data.map((d, i) => (typeof d.orgValue === "number" ? { x: xScale(i), y: yScale(d.orgValue) } : null));
  const userLevels = levels ? data.map((d) => getDistressLevel(d.userValue)) : null;

  const yTicks = [0, yMax / 2, yMax];
  const hovered = hoveredIndex != null ? data[hoveredIndex] : null;
  const hoveredLevel = userLevels ? userLevels[hoveredIndex] : null;

  return (
    <Box sx={{ position: "relative", overflowX: "auto" }}>
      <svg width={width} height={height} role="img" style={{ display: "block" }}>
        {/* 위험 단계 배경 밴드 */}
        {levels?.map((level) => (
          <rect
            key={level.label}
            x={padding.left}
            y={yScale(level.max)}
            width={innerWidth}
            height={Math.max(yScale(level.min) - yScale(level.max), 0)}
            fill={level.color}
            fillOpacity="0.1"
          />
        ))}

        {/* y축 그리드 + 라벨 */}
        {yTicks.map((tick) => (
          <g key={tick}>
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={yScale(tick)}
              y2={yScale(tick)}
              stroke={colorPalette.border}
              strokeDasharray="4 4"
            />
            <text x={padding.left - 6} y={yScale(tick) + 3} textAnchor="end" fontSize="10" fill={colorPalette.textSecondary}>
              {tick.toFixed(2)}
            </text>
          </g>
        ))}

        {/* 지역 평균 기준선 — 주간 변동 없는 고정값 */}
        {typeof regionAvg === "number" && (
          <g>
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={yScale(regionAvg)}
              y2={yScale(regionAvg)}
              stroke="#111827"
              strokeWidth="1.5"
              strokeDasharray="6 4"
            />
            <text
              x={width - padding.right}
              y={yScale(regionAvg) - 5}
              textAnchor="end"
              fontSize="10"
              fontWeight="700"
              fill="#111827"
            >
              {regionLabel} {regionAvg.toFixed(2)}
            </text>
          </g>
        )}

        {/* 기관 평균선 (점선) */}
        <path d={buildLinePath(orgPoints)} fill="none" stroke={color} strokeOpacity="0.45" strokeWidth="2" strokeDasharray="5 4" />
        {/* 개인선 (실선) */}
        <path d={buildLinePath(userPoints)} fill="none" stroke={color} strokeWidth="2.5" />

        {data.map((d, i) => (
          <g key={d.date ?? i}>
            {/* 히트 영역 (마우스오버 감지용, 화면에 보이지 않음) */}
            <rect
              x={xScale(i) - Math.max(xStep, 16) / 2}
              y={padding.top}
              width={Math.max(xStep, 16)}
              height={innerHeight}
              fill="transparent"
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex((prev) => (prev === i ? null : prev))}
              style={{ cursor: "pointer" }}
            />

            {/* 기관 평균 점 */}
            {orgPoints[i] && (
              <circle cx={orgPoints[i].x} cy={orgPoints[i].y} r="4" fill={color} fillOpacity="0.35" style={{ pointerEvents: "none" }} />
            )}

            {/* 개인 에피소드 발생일 강조 링 */}
            {d.isEpisode && userPoints[i] && (
              <circle cx={userPoints[i].x} cy={userPoints[i].y} r="9" fill="none" stroke="#ef4444" strokeWidth="2" style={{ pointerEvents: "none" }} />
            )}

            {/* 개인 점 - levels가 있으면 위험 단계 색상, 없으면 기본 색상 */}
            {userPoints[i] && (
              <circle
                cx={userPoints[i].x}
                cy={userPoints[i].y}
                r={hoveredIndex === i ? 6 : 5}
                fill={userLevels?.[i]?.color ?? color}
                stroke="#fff"
                strokeWidth="1.5"
                style={{ pointerEvents: "none", transition: "r 0.1s ease" }}
              />
            )}

            <text x={xScale(i)} y={height - padding.bottom + 16} textAnchor="middle" fontSize="10" fill={colorPalette.textSecondary}>
              {d.date ? d.date.slice(5) : ""}
            </text>
          </g>
        ))}
      </svg>

      {/* 커스텀 툴팁 — 차트 가장자리에서는 잘리지 않도록 위치/정렬을 뒤집음 */}
      {hovered && (() => {
        const tooltipLeft = xScale(hoveredIndex);
        const tooltipTop = Math.min(
          userPoints[hoveredIndex]?.y ?? Infinity,
          orgPoints[hoveredIndex]?.y ?? Infinity,
        );
        const alignX = tooltipLeft < 100 ? "start" : tooltipLeft > width - 100 ? "end" : "center";
        const translateX = alignX === "start" ? "0%" : alignX === "end" ? "-100%" : "-50%";
        const translateY = tooltipTop < 95 ? "20%" : "-130%";
        return (
          <Box
            sx={{
              position: "absolute",
              left: tooltipLeft,
              top: tooltipTop,
              transform: `translate(${translateX}, ${translateY})`,
              px: 1.2,
              py: 0.8,
              borderRadius: 1,
              backgroundColor: "#1a1f36",
              color: "#fff",
              fontSize: 11,
              fontWeight: 600,
              whiteSpace: "nowrap",
              pointerEvents: "none",
              boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
              zIndex: 1,
              lineHeight: 1.6,
            }}
          >
            <Box>{hovered.date}</Box>
            <Box>
              개인 {typeof hovered.userValue === "number" ? hovered.userValue.toFixed(2) : "-"}
              {hoveredLevel ? ` (${hoveredLevel.label})` : ""}
              {hovered.isEpisode ? " · 에피소드" : ""}
            </Box>
            <Box>기관 평균 {typeof hovered.orgValue === "number" ? hovered.orgValue.toFixed(2) : "-"}</Box>
          </Box>
        );
      })()}
    </Box>
  );
};

/**
 * 기간별 평균 점수 꺾은선 그래프 (외부 차트 라이브러리 없이 순수 SVG로 구현)
 * x축: 검사/집계 날짜 / y축: 그날의 평균 점수 / 점 크기: 그날의 검사(또는 에피소드) 횟수
 */
const TrendLineChart = ({ data, color, maxY, regionAvg, regionLabel = "지역 평균" }) => {
  const height = 220;
  const padding = { top: 16, right: 16, bottom: 32, left: 32 };
  const [hoveredIndex, setHoveredIndex] = useState(null);

  if (data.length === 0) {
    return (
      <Box
        sx={{
          py: 5,
          textAlign: "center",
          border: `1px dashed ${colorPalette.border}`,
          borderRadius: 1,
          color: colorPalette.textSecondary,
        }}
      >
        <Typography variant="body2">표시할 데이터가 없습니다.</Typography>
      </Box>
    );
  }

  // 값이 없더라도 채운 날짜가 많아지면(기간이 길수록) SVG 전체 너비를 늘려서 x축 라벨이 겹치지 않게 함
  const width = Math.max(MIN_CHART_WIDTH, padding.left + padding.right + (data.length - 1) * MIN_X_STEP);
  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;

  const xStep = data.length > 1 ? innerWidth / (data.length - 1) : 0;
  const xScale = (index) => padding.left + xStep * index;
  const yScale = (value) => padding.top + innerHeight - (value / maxY) * innerHeight;

  const maxCount = Math.max(...data.map((d) => d.count), 0);
  // 검사(또는 에피소드) 횟수가 많을수록 점이 커지도록 반지름을 6~16px 범위로 스케일링
  const radiusFor = (count) => (maxCount <= 1 ? 8 : 6 + (count / maxCount) * 10);

  // 값이 없는(avg: null) 날짜는 점을 찍지 않지만, 선은 끊지 않고 실제 값이 있는 지점끼리 바로 이어줌
  const points = data.map((d, i) => (typeof d.avg === "number" ? { x: xScale(i), y: yScale(d.avg) } : null));
  const linePath = buildLinePath(points.filter(Boolean));

  const yTicks = [0, maxY / 2, maxY];

  const hovered = hoveredIndex != null ? data[hoveredIndex] : null;

  return (
    <Box sx={{ position: "relative", overflowX: "auto" }}>
      <svg width={width} height={height} role="img" style={{ display: "block" }}>
        {/* y축 그리드 + 라벨 */}
        {yTicks.map((tick) => (
          <g key={tick}>
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={yScale(tick)}
              y2={yScale(tick)}
              stroke={colorPalette.border}
              strokeDasharray="4 4"
            />
            <text
              x={padding.left - 6}
              y={yScale(tick) + 3}
              textAnchor="end"
              fontSize="10"
              fill={colorPalette.textSecondary}
            >
              {tick % 1 === 0 ? tick : tick.toFixed(2)}
            </text>
          </g>
        ))}

        {/* 지역 평균 기준선 — 주간 변동 없는 고정값 (DailyDualLineChart와 동일한 스타일) */}
        {typeof regionAvg === "number" && (
          <g>
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={yScale(regionAvg)}
              y2={yScale(regionAvg)}
              stroke="#111827"
              strokeWidth="1.5"
              strokeDasharray="6 4"
            />
            <text
              x={width - padding.right}
              y={yScale(regionAvg) - 5}
              textAnchor="end"
              fontSize="10"
              fontWeight="700"
              fill="#111827"
            >
              {regionLabel} {regionAvg.toFixed(2)}
            </text>
          </g>
        )}

        {/* 평균 점수 추이선 */}
        <path d={linePath} fill="none" stroke={color} strokeWidth="2" />

        {/* 데이터 포인트 (원 크기 = 검사/에피소드 횟수) + x축 날짜 라벨 — 값이 없는 날짜는 점 없이 라벨만 표시 */}
        {data.map((d, i) => (
          <g key={d.date}>
            {typeof d.avg === "number" && (
              <>
                {/* 실제 원보다 넉넉한 투명 히트 영역 — 원이 작아도 마우스오버가 잘 걸리도록 함 */}
                <circle
                  cx={xScale(i)}
                  cy={yScale(d.avg)}
                  r={Math.max(radiusFor(d.count), 12)}
                  fill="transparent"
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex((prev) => (prev === i ? null : prev))}
                  style={{ cursor: "pointer" }}
                />
                <circle
                  cx={xScale(i)}
                  cy={yScale(d.avg)}
                  r={radiusFor(d.count)}
                  fill={color}
                  fillOpacity={hoveredIndex === i ? 0.95 : 0.75}
                  stroke="#fff"
                  strokeWidth={hoveredIndex === i ? 2.5 : 1.5}
                  style={{ pointerEvents: "none", transition: "fill-opacity 0.1s ease" }}
                />
              </>
            )}
            <text
              x={xScale(i)}
              y={height - padding.bottom + 16}
              textAnchor="middle"
              fontSize="10"
              fill={colorPalette.textSecondary}
            >
              {d.date ? d.date.slice(5) : ""}
            </text>
          </g>
        ))}
      </svg>

      {/* 커스텀 툴팁 — hoveredIndex가 있을 때만 데이터 포인트 위에 표시. 차트 가장자리에서는 잘리지 않도록 위치/정렬을 뒤집음 */}
      {hovered && (() => {
        const tooltipLeft = xScale(hoveredIndex);
        const tooltipTop = yScale(hovered.avg);
        const alignX = tooltipLeft < 90 ? "start" : tooltipLeft > width - 90 ? "end" : "center";
        const translateX = alignX === "start" ? "0%" : alignX === "end" ? "-100%" : "-50%";
        const translateY = tooltipTop < 55 ? "20%" : "-130%";
        return (
          <Box
            sx={{
              position: "absolute",
              left: tooltipLeft,
              top: tooltipTop,
              transform: `translate(${translateX}, ${translateY})`,
              px: 1.2,
              py: 0.7,
              borderRadius: 1,
              backgroundColor: "#1a1f36",
              color: "#fff",
              fontSize: 11,
              fontWeight: 600,
              whiteSpace: "nowrap",
              pointerEvents: "none",
              boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
              zIndex: 1,
            }}
          >
            {hovered.date} · 평균 {hovered.avg} · {hovered.count}건
          </Box>
        );
      })()}
    </Box>
  );
};

/** 4단계 위험 구간 범례 (색상 스와치 + 점수 범위 + 라벨) */
const DistressLevelLegend = () => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1 }}>
    {DISTRESS_LEVELS.map((level) => (
      <Box key={level.label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: level.color, flexShrink: 0 }} />
        <Typography variant="caption" sx={{ color: colorPalette.textSecondary }}>
          {level.min.toFixed(2)} ~ {level.max.toFixed(2)} · {level.label}
        </Typography>
      </Box>
    ))}
  </Box>
);

/** 참가자 명단의 "위험" 배지에 마우스오버 시 보여줄 감지 요약 (suicide-risk API 응답 1건) */
const RiskTooltipContent = ({ risk }) => (
  <Box sx={{ p: 0.5, fontSize: 12 }}>
    <Box>최초 감지: {formatDate(risk.detected_at)}</Box>
    <Box>최근 감지: {formatDate(risk.last_detected_at)}</Box>
    <Box>
      최초 감지 후 {risk.days_since_detected ?? "-"}일 · 최근 감지 후 {risk.days_since_last ?? "-"}일 경과
    </Box>
    {risk.note && <Box>메모: {risk.note}</Box>}
  </Box>
);

/** 상세 패널 상단에 노출되는 자살/자해 위험 감지 정보 카드 (suicide-risk API 응답 1건) */
const RiskDetailCard = ({ risk }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      mb: 3,
      backgroundColor: "#fff5f5",
      borderColor: "#ff3b30",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
      <WarningAmberIcon fontSize="small" sx={{ color: "#ff3b30" }} />
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#ff3b30" }}>
        자살·자해 위험 감지 정보
      </Typography>
    </Box>
    {risk ? (
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 1.5 }}>
        {[
          { label: "상태", value: risk.status ?? "-" },
          { label: "최초 감지", value: formatDate(risk.detected_at) },
          { label: "최근 감지", value: formatDate(risk.last_detected_at) },
          { label: "최초 감지 후 경과", value: `${risk.days_since_detected ?? "-"}일` },
          { label: "최근 감지 후 경과", value: `${risk.days_since_last ?? "-"}일` },
          { label: "메모", value: risk.note ?? "-" },
        ].map((field) => (
          <Paper key={field.label} variant="outlined" sx={{ p: 1.5, backgroundColor: colorPalette.white }}>
            <Typography
              variant="caption"
              sx={{ display: "block", color: colorPalette.textSecondary, fontWeight: 600 }}
            >
              {field.label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: colorPalette.textPrimary }}>
              {field.value}
            </Typography>
          </Paper>
        ))}
      </Box>
    ) : (
      <Typography variant="caption" sx={{ color: colorPalette.textSecondary }}>
        감지 상세 정보를 불러오는 중이거나 확인할 수 없습니다.
      </Typography>
    )}
  </Paper>
);

const UserDetailView = ({
  project,
  participants = [],
  stats,
  loading,
  error,
  statsLoading,
  statsError,
  isMasterAdmin,
  riskDetailsByUserId = {},
  hideRiskInfo = false,
}) => {
  const [tab, setTab] = useState(0);
  const viewConfig = useMemo(() => getViewConfig(project), [project]);

  // 소속/권한에 따라 "만족도조사 통계" 탭 노출 여부 결정
  const showSatisfactionTab = shouldShowSatisfactionTab(project?.organizationName, isMasterAdmin);

  // 한국판 정신적 웰빙 척도는 GROW2026(대구한의대학교) 사업에서만 노출
  const showWellbeing = project?.code === WELLBEING_PROJECT_CODE;

  // 소속이 바뀌어 만족도조사 탭이 사라지는 경우, 그 탭을 보고 있었다면 목록 탭으로 복귀
  useEffect(() => {
    if (!showSatisfactionTab && tab === 2) {
      setTab(0);
    }
  }, [showSatisfactionTab, tab]);

  // 목록 탭 상태
  const [keyword, setKeyword] = useState("");
  const [genderFilter, setGenderFilter] = useState("전체");
  const [testFilter, setTestFilter] = useState("전체");
  const [classFilter, setClassFilter] = useState("전체");
  const [currentPage, setCurrentPage] = useState(1);
  const participantListRef = useRef(null);

  // 페이지 번호가 바뀌면 참가자 명단 스크롤을 맨 위로 이동
  useEffect(() => {
    participantListRef.current?.scrollTo({ top: 0 });
  }, [currentPage]);
  const [selectedParticipant, setSelectedParticipant] = useState(null);

  // 선택된 참가자의 상세 (program-detail API 응답: phq/gad 회차 이력 + 주간 지표)
  const [programDetail, setProgramDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // 상세 조회 기간 - 비워두면 서버 기본값(최근 7일)으로 조회됨
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");

  // 프로젝트가 바뀌면 목록/선택 상태 초기화
  useEffect(() => {
    setKeyword("");
    setGenderFilter("전체");
    setTestFilter("전체");
    setClassFilter("전체");
    setCurrentPage(1);
    setSelectedParticipant(null);
  }, [project?.code]);

  // 월성복지관 소속 참가자는 program-detail이 아닌 전용 API(ws-detail)로 조회 (다른 소속은 기존 program-detail 유지)
  const fetchProgramDetail = useCallback((userId, code, organizationName, startDate, endDate) => {
    setDetailLoading(true);
    setProgramDetail(null);

    const request =
      organizationName === "월성복지관"
        ? wsApi.getUserWsDetail(userId, code)
        : wsApi.getUserProgramDetail(userId, code, startDate || undefined, endDate || undefined);

    request
      .then((res) => {
        setProgramDetail(res.data);
      })
      .catch((err) => {
        // console.error("[UserDetailView] 참가자 상세 조회 실패:", err);
      })
      .finally(() => {
        setDetailLoading(false);
      });
  }, []);

  // 참가자/프로젝트가 바뀌면 기간 입력을 초기화하고 기본(최근 7일) 기준으로 다시 조회
  useEffect(() => {
    const userId = selectedParticipant?.user_id;
    setRangeStart("");
    setRangeEnd("");

    if (!userId || !project?.code) {
      setProgramDetail(null);
      return;
    }

    fetchProgramDetail(userId, project.code, project?.organizationName);
  }, [selectedParticipant, project?.code, project?.organizationName, fetchProgramDetail]);

  // "조회" 버튼 클릭 시에만 선택한 기간으로 재조회 (입력 중에는 호출하지 않음)
  const handleRangeSearch = () => {
    const userId = selectedParticipant?.user_id;
    if (!userId || !project?.code) return;
    fetchProgramDetail(userId, project.code, project?.organizationName, rangeStart, rangeEnd);
  };

  // invite code에서 반(class) 정보가 파싱되는 참가자가 있을 때만 반 필터를 노출
  const classOptions = useMemo(() => {
    const byLabel = new Map();
    participants.forEach((p) => {
      const info = parseClassInfo(p.registered_invite_code, project?.code);
      if (info && !byLabel.has(info.label)) byLabel.set(info.label, info);
    });
    return Array.from(byLabel.values()).sort((a, b) => {
      const periodOrder = { S: 0, M: 1, L: 2 };
      const periodDiff = (periodOrder[a.periodLetter] ?? 9) - (periodOrder[b.periodLetter] ?? 9);
      return periodDiff !== 0 ? periodDiff : a.classNumber - b.classNumber;
    });
  }, [participants, project?.code]);

  // 반 필터가 노출되지 않는 프로젝트로 이동했다면 선택값 초기화
  useEffect(() => {
    if (classOptions.length === 0) setClassFilter("전체");
  }, [classOptions]);

  // 검색/필터 적용
  const filteredList = useMemo(() => {
    return participants.filter((p) => {
      if (keyword && !p.name?.includes(keyword.trim())) return false;
      if (genderFilter !== "전체" && genderLabel(p.gender) !== genderFilter) return false;
      if (testFilter === "PHQ_ANY" && !(p.phq_count > 0)) return false;
      if (testFilter === "GAD_ANY" && !(p.gad_count > 0)) return false;
      if (testFilter === "WELLBEING_ANY" && !(p.wellbeing_count > 0)) return false;
      if (testFilter === "NONE" && (p.phq_count > 0 || p.gad_count > 0 || p.wellbeing_count > 0)) return false;
      if (classFilter !== "전체") {
        const info = parseClassInfo(p.registered_invite_code, project?.code);
        if (info?.label !== classFilter) return false;
      }
      return true;
    });
  }, [participants, keyword, genderFilter, testFilter, classFilter, project?.code]);

  const totalCount = filteredList.length;
  const pageCnt = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pagedList = filteredList.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  // 번호형 페이지네이션 — 현재 페이지가 속한 그룹(최대 DISPLAY_PAGE_COUNT개)의 번호만 표시
  const pageGroup = Math.ceil(currentPage / DISPLAY_PAGE_COUNT);
  const startPage = (pageGroup - 1) * DISPLAY_PAGE_COUNT + 1;
  const endPage = Math.min(pageGroup * DISPLAY_PAGE_COUNT, pageCnt);

  const handleFilterChange = (setter) => (value) => {
    setter(value);
    setCurrentPage(1);
  };

  const handleSelectParticipant = (participant) => {
    setSelectedParticipant((prev) =>
      prev?.user_id === participant.user_id ? null : participant,
    );
  };

  // ── 검사 통계 탭 (서버 stats 응답 기반) ──
  const countStatCards = [
    { key: "participant_count", label: "참가자 수", value: `${stats?.participant_count ?? 0}명` },
    { key: "phq_count", label: "PHQ-9 누적 검사 횟수", value: `${stats?.phq?.total ?? 0}회`, weekNew: stats?.phq?.week_new },
    { key: "gad_count", label: "GAD-7 누적 검사 횟수", value: `${stats?.gad?.total ?? 0}회`, weekNew: stats?.gad?.week_new },
    ...(showWellbeing
      ? [{ key: "wellbeing_count", label: "웰빙 척도 누적 검사 횟수", value: `${stats?.wellbeing?.total ?? 0}회`, weekNew: stats?.wellbeing?.week_new }]
      : []),
    { key: "unexamined_count", label: "미검사 참가자 수", value: `${stats?.unexamined_count ?? 0}명` },
    { key: "risk_count", label: "자살위험군 인원", value: `${stats?.suicide_risk_count ?? 0}명` },
    { key: "episode_total", label: "이번 주 에피소드 발생", value: `${stats?.episode_total ?? 0}건` },
  ];

  const scoreTableRows = stats?.score_table ?? [];

  const phqTrend = useMemo(
    () => fillTrendDateGaps(normalizeTrend(stats?.phq_trend, "average_score", "count")),
    [stats?.phq_trend],
  );
  const gadTrend = useMemo(
    () => fillTrendDateGaps(normalizeTrend(stats?.gad_trend, "average_score", "count")),
    [stats?.gad_trend],
  );
  const wellbeingTrend = useMemo(
    () => fillTrendDateGaps(normalizeTrend(stats?.wellbeing_trend, "average_score", "count")),
    [stats?.wellbeing_trend],
  );
  const symptomTrend = useMemo(
    () => fillTrendDateGaps(normalizeTrend(stats?.daily_scores, "symptom_avg", "episode_count")),
    [stats?.daily_scores],
  );
  const emoTrend = useMemo(
    () => fillTrendDateGaps(normalizeTrend(stats?.daily_scores, "emo_avg", "episode_count")),
    [stats?.daily_scores],
  );
  const distressTrend = useMemo(
    () => fillTrendDateGaps(normalizeTrend(stats?.daily_scores, "distress_avg", "episode_count")),
    [stats?.daily_scores],
  );

  // 개인 상세 차트(DailyDualLineChart)와 동일한 방식(데이터 최댓값 * 1.25, 지역 평균 포함)으로 y축 상한을 동적 계산해 두 차트의 스케일을 통일
  const symptomMaxY = useMemo(() => computeDynamicMaxY(symptomTrend, REGION_AVERAGE.symptomAvg), [symptomTrend]);
  const emoMaxY = useMemo(() => computeDynamicMaxY(emoTrend), [emoTrend]);
  const distressMaxY = useMemo(() => computeDynamicMaxY(distressTrend, REGION_AVERAGE.distressAvg), [distressTrend]);

  return (
    <Paper
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: colorPalette.white,
      }}
    >
      {/* 헤더: 타이틀 + 탭 */}
      <Box
        sx={{
          px: 3,
          pt: 2.5,
          borderBottom: `1px solid ${colorPalette.border}`,
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: colorPalette.textPrimary }}>
          {viewConfig.title}
        </Typography>
        <Typography
          variant="body2"
          sx={{ mt: 0.5, mb: 1.5, color: colorPalette.textSecondary, fontSize: 13 }}
        >
          {viewConfig.description}
        </Typography>

        <Tabs
          value={tab}
          onChange={(_, newVal) => setTab(newVal)}
          sx={{
            minHeight: 36,
            "& .MuiTab-root": {
              minHeight: 36,
              fontSize: 13,
              fontWeight: 600,
              textTransform: "none",
              color: colorPalette.textSecondary,
            },
            "& .Mui-selected": { color: `${colorPalette.primary} !important` },
            "& .MuiTabs-indicator": { backgroundColor: colorPalette.primary },
          }}
        >
          <Tab label="참가자 목록" />
          <Tab label="검사 통계" />
          {showSatisfactionTab && <Tab label="만족도조사 통계" />}
        </Tabs>
      </Box>

      <>
        {/* 탭 1: 참가자 목록 — 왼쪽 명단 사이드바 + 오른쪽 상세 패널 (로딩/에러는 참가자 목록 조회 기준) */}
        {tab === 0 && (loading ? (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography variant="body2" sx={{ color: colorPalette.textSecondary }}>
              {error}
            </Typography>
          </Box>
        ) : (
            <Box sx={{ flex: 1, display: "flex", gap: 2, overflow: "hidden", p: 2 }}>
              {/* 왼쪽: 참가자 명단 사이드바 */}
              <Paper
                variant="outlined"
                sx={{
                  width: 280,
                  flexShrink: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  backgroundColor: colorPalette.white,
                }}
              >
                {/* 검색 & 필터 */}
                <Box
                  sx={{
                    p: 2,
                    borderBottom: `1px solid ${colorPalette.border}`,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    flexShrink: 0,
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="이름 검색"
                    value={keyword}
                    onChange={(e) => handleFilterChange(setKeyword)(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: colorPalette.textSecondary }} />
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                  />
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Select
                      size="small"
                      value={genderFilter}
                      onChange={(e) => handleFilterChange(setGenderFilter)(e.target.value)}
                      sx={{ flex: 1, fontSize: 13 }}
                    >
                      <MenuItem value="전체">성별 전체</MenuItem>
                      <MenuItem value="남">남</MenuItem>
                      <MenuItem value="여">여</MenuItem>
                    </Select>
                    <Select
                      size="small"
                      value={testFilter}
                      onChange={(e) => handleFilterChange(setTestFilter)(e.target.value)}
                      sx={{ flex: 1.6, fontSize: 13 }}
                    >
                      <MenuItem value="전체">검사이력 전체</MenuItem>
                      <MenuItem value="PHQ_ANY">PHQ-9 있음</MenuItem>
                      <MenuItem value="GAD_ANY">GAD-7 있음</MenuItem>
                      {showWellbeing && <MenuItem value="WELLBEING_ANY">웰빙 척도 있음</MenuItem>}
                      <MenuItem value="NONE">이력 없음</MenuItem>
                    </Select>
                  </Box>
                  {classOptions.length > 0 && (
                    <Select
                      size="small"
                      value={classFilter}
                      onChange={(e) => handleFilterChange(setClassFilter)(e.target.value)}
                      fullWidth
                      sx={{ fontSize: 13 }}
                    >
                      <MenuItem value="전체">반 전체</MenuItem>
                      {classOptions.map((option) => (
                        <MenuItem key={option.label} value={option.label}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                  <Typography variant="caption" sx={{ color: colorPalette.textSecondary }}>
                    총 {totalCount}명
                  </Typography>
                </Box>

                {/* 참가자 명단 */}
                <List ref={participantListRef} sx={{ flex: 1, overflowY: "auto", p: 0 }}>
                  {pagedList.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: "center", color: colorPalette.textSecondary }}>
                      <Typography variant="body2">조회된 참가자가 없습니다.</Typography>
                    </Box>
                  ) : (
                    pagedList.map((row) => {
                      const isSelected = selectedParticipant?.user_id === row.user_id;
                      const classInfo = parseClassInfo(row.registered_invite_code, project?.code);
                      return (
                        <ListItemButton
                          key={row.user_id}
                          selected={isSelected}
                          onClick={() => handleSelectParticipant(row)}
                          sx={{
                            borderBottom: `1px solid ${colorPalette.border}`,
                            backgroundColor: isSelected ? colorPalette.activeBg : "transparent",
                            "&.Mui-selected": { backgroundColor: colorPalette.activeBg },
                            "&:hover": { backgroundColor: colorPalette.activeBg },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: 14,
                                    fontWeight: isSelected ? 700 : 500,
                                    color: isSelected ? colorPalette.primary : colorPalette.textPrimary,
                                  }}
                                >
                                  {row.name}
                                </Typography>
                                {row.has_suicide_risk && !hideRiskInfo && (
                                  <Tooltip
                                    title={
                                      riskDetailsByUserId[row.user_id] ? (
                                        <RiskTooltipContent risk={riskDetailsByUserId[row.user_id]} />
                                      ) : (
                                        "자살·자해 위험이 감지된 참가자입니다."
                                      )
                                    }
                                    arrow
                                  >
                                    <Chip
                                      label="위험"
                                      size="small"
                                      sx={{
                                        height: 18,
                                        fontSize: 10,
                                        fontWeight: 700,
                                        backgroundColor: "#ff3b30",
                                        color: "#fff",
                                        cursor: "default",
                                      }}
                                    />
                                  </Tooltip>
                                )}
                                {row.app_usage && (
                                  <Chip
                                    label={row.app_usage}
                                    size="small"
                                    sx={{
                                      height: 18,
                                      fontSize: 10,
                                      fontWeight: 700,
                                      ...getAppUsageStyle(row.app_usage),
                                    }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={`${classInfo ? `${classInfo.label} · ` : ""}${ageLabel(row.age)} · ${genderLabel(row.gender)} · PHQ ${row.phq_count ?? 0}회 · GAD ${row.gad_count ?? 0}회${showWellbeing ? ` · 웰빙 ${row.wellbeing_count ?? 0}회` : ""}`}
                            secondaryTypographyProps={{ fontSize: 12, color: colorPalette.textSecondary }}
                          />
                        </ListItemButton>
                      );
                    })
                  )}
                </List>

                {/* 페이지네이션 — 버튼을 숨기지 않고 disabled 처리해 번호 위치가 고정되도록 함 */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.3,
                    justifyContent: "center",
                    alignItems: "center",
                    py: 1.2,
                    px: 1,
                    borderTop: `1px solid ${colorPalette.border}`,
                    flexShrink: 0,
                  }}
                >
                  <Button
                    size="small"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(1)}
                    sx={{ minWidth: 26, px: 0.5, fontSize: 12 }}
                  >
                    &lt;&lt;
                  </Button>
                  <Button
                    size="small"
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    sx={{ minWidth: 26, px: 0.5, fontSize: 12 }}
                  >
                    &lt;
                  </Button>
                  {Array.from({ length: endPage - startPage + 1 }, (_, index) => {
                    const pageNumber = startPage + index;
                    const isActive = currentPage === pageNumber;

                    return (
                      <Button
                        key={pageNumber}
                        size="small"
                        variant={isActive ? "contained" : "text"}
                        onClick={() => setCurrentPage(pageNumber)}
                        sx={{
                          minWidth: 26,
                          px: 0.5,
                          fontSize: 12,
                          color: isActive ? colorPalette.white : colorPalette.textSecondary,
                        }}
                      >
                        {pageNumber}
                      </Button>
                    );
                  })}
                  <Button
                    size="small"
                    disabled={currentPage >= pageCnt}
                    onClick={() => setCurrentPage((prev) => Math.min(pageCnt, prev + 1))}
                    sx={{ minWidth: 26, px: 0.5, fontSize: 12 }}
                  >
                    &gt;
                  </Button>
                  <Button
                    size="small"
                    disabled={currentPage === pageCnt}
                    onClick={() => setCurrentPage(pageCnt)}
                    sx={{ minWidth: 26, px: 0.5, fontSize: 12 }}
                  >
                    &gt;&gt;
                  </Button>
                </Box>
              </Paper>

              {/* 오른쪽: 선택된 참가자 상세 (program-detail API 연동) */}
              <Box sx={{ flex: 1, overflowY: "auto", pr: 1 }}>
                {!selectedParticipant ? (
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: `1px dashed ${colorPalette.border}`,
                      borderRadius: 1,
                      color: colorPalette.textSecondary,
                    }}
                  >
                    <Typography variant="body2">왼쪽 명단에서 참가자를 선택하면 검사 내역이 표시됩니다.</Typography>
                  </Box>
                ) : detailLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: colorPalette.textPrimary }}>
                        {programDetail?.name ?? selectedParticipant.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: colorPalette.textSecondary }}>
                        {ageLabel(programDetail?.age ?? selectedParticipant.age)} ·{" "}
                        {genderLabel(programDetail?.gender ?? selectedParticipant.gender)}
                      </Typography>
                    </Box>

                    {selectedParticipant.has_suicide_risk && !hideRiskInfo && (
                      <RiskDetailCard risk={riskDetailsByUserId[selectedParticipant.user_id]} />
                    )}

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: colorPalette.textPrimary }}>
                      검사 내역
                    </Typography>
                    <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 4 }}>
                      <HistoryList
                        key={`phq-${selectedParticipant.user_id}`}
                        label="PHQ-9 우울증 선별검사"
                        type="phq"
                        history={programDetail?.phq_history}
                        userId={selectedParticipant.user_id}
                      />
                      <HistoryList
                        key={`gad-${selectedParticipant.user_id}`}
                        label="GAD-7 불안장애 선별검사"
                        type="gad"
                        history={programDetail?.gad_history}
                        userId={selectedParticipant.user_id}
                      />
                    </Box>

                    {project?.organizationName === "월성복지관" && (
                      <Box sx={{ mb: 4 }}>
                        <SatisfactionSurveyList
                          surveys={programDetail?.satisfaction_surveys}
                          userId={selectedParticipant.user_id}
                          participantName={programDetail?.name ?? selectedParticipant.name}
                          participantLabel={`${programDetail?.name ?? selectedParticipant.name ?? ""} (${ageLabel(programDetail?.age ?? selectedParticipant.age)} · ${genderLabel(programDetail?.gender ?? selectedParticipant.gender)})`}
                        />
                      </Box>
                    )}

                    {showWellbeing && (
                      <SubScoreHistoryList
                        key={`wellbeing-${selectedParticipant.user_id}`}
                        title="한국판 정신적 웰빙 척도"
                        items={programDetail?.wellbeing_history}
                        surveyType="wellbeing"
                        userId={selectedParticipant.user_id}
                      />
                    )}

                    {project?.organizationName === "월성복지관" && (
                      <SubScoreHistoryList title="취업준비도 (K-EHS)" items={programDetail?.employment_readiness} />
                    )}

                    {!hideRiskInfo && (
                      <>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 1.5,
                            mb: 1.5,
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colorPalette.textPrimary }}>
                            주간 요약
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
                            <TextField
                              type="date"
                              size="small"
                              value={rangeStart}
                              onChange={(e) => setRangeStart(e.target.value)}
                              InputLabelProps={{ shrink: true }}
                              sx={{ "& .MuiInputBase-input": { fontSize: 13, py: 0.7 } }}
                            />
                            <Typography variant="caption" sx={{ color: colorPalette.textSecondary }}>
                              ~
                            </Typography>
                            <TextField
                              type="date"
                              size="small"
                              value={rangeEnd}
                              onChange={(e) => setRangeEnd(e.target.value)}
                              InputLabelProps={{ shrink: true }}
                              sx={{ "& .MuiInputBase-input": { fontSize: 13, py: 0.7 } }}
                            />
                            <Button
                              size="small"
                              variant="contained"
                              onClick={handleRangeSearch}
                              sx={{ fontSize: 12, px: 1.5 }}
                            >
                              조회
                            </Button>
                          </Box>
                        </Box>
                        <Typography variant="caption" sx={{ display: "block", mb: 1.5, color: colorPalette.textSecondary }}>
                          기간을 비워두고 조회하면 최근 7일 기준으로 다시 표시됩니다. 선택한 기간에 따라 아래 지표 일수는 7일보다 많거나 적을 수 있습니다.
                        </Typography>
                        <Box sx={{ mb: 4 }}>
                          <KeyValueSummary data={programDetail?.week} />
                        </Box>
                      </>
                    )}

                    {!hideRiskInfo && (
                      <>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colorPalette.textPrimary }}>
                          날짜별 증상 · 감정 · 디스트레스 지표
                        </Typography>
                        <Typography variant="caption" sx={{ display: "block", mt: 0.5, mb: 2, color: colorPalette.textSecondary }}>
                          실선은 개인, 점선은 기관 평균, 검은 점선은 지역 평균(고정값)입니다. 빨간 테두리는 개인에게 그날 에피소드가 발생했음을 의미합니다.
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                          <Paper variant="outlined" sx={{ p: 2, backgroundColor: colorPalette.white }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: colorPalette.textPrimary, fontSize: 13 }}>
                              증상 점수
                            </Typography>
                            <DailyDualLineChart
                              data={buildDailyDualSeries(programDetail?.daily_scores, "symptom_score", "org_symptom_avg")}
                              color={colorPalette.primary}
                              regionAvg={REGION_AVERAGE.symptomAvg}
                            />
                          </Paper>
                          <Paper variant="outlined" sx={{ p: 2, backgroundColor: colorPalette.white }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: colorPalette.textPrimary, fontSize: 13 }}>
                              감정 점수
                            </Typography>
                            <DailyDualLineChart
                              data={buildDailyDualSeries(programDetail?.daily_scores, "emo_score", "org_emo_avg")}
                              color="#f59e0b"
                            />
                          </Paper>
                          <Paper variant="outlined" sx={{ p: 2, backgroundColor: colorPalette.white }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: colorPalette.textPrimary, fontSize: 13 }}>
                              디스트레스 점수
                            </Typography>
                            <DailyDualLineChart
                              data={buildDailyDualSeries(programDetail?.daily_scores, "distress_score", "org_distress_avg")}
                              color="#8b5cf6"
                              levels={DISTRESS_LEVELS}
                              regionAvg={REGION_AVERAGE.distressAvg}
                            />
                            <DistressLevelLegend />
                          </Paper>
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
        ))}

        {/* 탭 2: 검사 통계 — 로딩/에러는 별도 program-stats API 기준 */}
        <TabPanel value={tab} index={1}>
          {statsLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 6 }}>
              <CircularProgress size={24} />
            </Box>
          ) : statsError ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 6 }}>
              <Typography variant="body2" sx={{ color: colorPalette.textSecondary }}>
                {statsError}
              </Typography>
            </Box>
          ) : (
            <>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: colorPalette.textPrimary }}>
              검사 횟수 통계
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: 2,
                mb: 4,
              }}
            >
              {countStatCards.map((card) => (
                <Paper
                  key={card.key}
                  variant="outlined"
                  sx={{ p: 2.5, backgroundColor: colorPalette.white }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: colorPalette.textSecondary, fontWeight: 600 }}
                  >
                    {card.label}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, mt: 0.5 }}>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 700, color: colorPalette.textPrimary }}
                    >
                      {card.value}
                    </Typography>
                    {"weekNew" in card && (
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, color: card.weekNew ? "#f59e0b" : colorPalette.textSecondary }}
                      >
                        {card.weekNew ? `(▲ ${card.weekNew})` : "( - )"}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>

            {/* 기간별 평균 점수 추이 그래프 */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: colorPalette.textPrimary }}>
              기간별 평균 점수 추이
            </Typography>
            <Typography variant="caption" sx={{ display: "block", mb: 2, color: colorPalette.textSecondary }}>
              점 크기는 해당 날짜의 검사 횟수에 비례합니다. 점 위에 마우스를 올리면 상세 수치가 표시됩니다.
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mb: 4 }}>
              <Paper variant="outlined" sx={{ p: 2, backgroundColor: colorPalette.white }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: colorPalette.textPrimary, fontSize: 13 }}>
                  PHQ-9 평균 점수
                </Typography>
                <TrendLineChart data={phqTrend} color={colorPalette.primary} maxY={ASSESSMENT_MAX_SCORE.phq} />
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, backgroundColor: colorPalette.white }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: colorPalette.textPrimary, fontSize: 13 }}>
                  GAD-7 평균 점수
                </Typography>
                <TrendLineChart data={gadTrend} color="#f59e0b" maxY={ASSESSMENT_MAX_SCORE.gad} />
              </Paper>
              {showWellbeing && (
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: colorPalette.white }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: colorPalette.textPrimary, fontSize: 13 }}>
                    한국판 정신적 웰빙 척도 평균 점수
                  </Typography>
                  <TrendLineChart data={wellbeingTrend} color="#10b981" maxY={ASSESSMENT_MAX_SCORE.wellbeing} />
                </Paper>
              )}
            </Box>

            {/* 검사 점수 통계 테이블 */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: colorPalette.textPrimary }}>
              검사 점수 통계
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 110px 110px 110px 110px",
                alignItems: "center",
                px: 2,
                py: 1.5,
                borderRadius: 1,
                backgroundColor: colorPalette.background,
                border: `1px solid ${colorPalette.border}`,
              }}
            >
              {["검사 종류", "검사 횟수", "평균 점수", "최고 점수", "최저 점수"].map((label) => (
                <Typography
                  key={label}
                  variant="caption"
                  sx={{ color: colorPalette.textSecondary, fontWeight: 700, textAlign: "center" }}
                >
                  {label}
                </Typography>
              ))}
            </Box>
            <Box sx={{ mt: 1, mb: 4, display: "flex", flexDirection: "column", gap: 1 }}>
              {scoreTableRows.length === 0 ? (
                <Box
                  sx={{
                    py: 3,
                    textAlign: "center",
                    border: `1px dashed ${colorPalette.border}`,
                    borderRadius: 1,
                    color: colorPalette.textSecondary,
                  }}
                >
                  <Typography variant="caption">표시할 검사 점수 통계가 없습니다.</Typography>
                </Box>
              ) : (
                scoreTableRows.map((row) => (
                  <Box
                    key={row.title}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 110px 110px 110px 110px",
                      alignItems: "center",
                      px: 2,
                      py: 1.8,
                      border: `1px solid ${colorPalette.border}`,
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, color: colorPalette.textPrimary, fontSize: 13 }}>
                      {row.title}
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: "center", color: colorPalette.textSecondary, fontSize: 13 }}>
                      {row.count}회
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: "center", color: colorPalette.textSecondary, fontSize: 13 }}>
                      {row.average}
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: "center", color: colorPalette.textSecondary, fontSize: 13 }}>
                      {row.max_score}
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: "center", color: colorPalette.textSecondary, fontSize: 13 }}>
                      {row.min_score}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>

            {/* 최근 7일 증상 · 디스트레스 지표 (기관 전체 평균) */}
            {!hideRiskInfo && (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: colorPalette.textPrimary }}>
                  최근 7일 증상 · 감정 · 디스트레스 지표 (기관 전체)
                </Typography>
                <Typography variant="caption" sx={{ display: "block", mb: 2, color: colorPalette.textSecondary }}>
                  점 크기는 해당 날짜의 에피소드 발생 건수에 비례합니다.
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <Paper variant="outlined" sx={{ p: 2, backgroundColor: colorPalette.white }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: colorPalette.textPrimary, fontSize: 13 }}>
                      증상 점수 평균
                    </Typography>
                    <TrendLineChart
                      data={symptomTrend}
                      color={colorPalette.primary}
                      maxY={symptomMaxY}
                      regionAvg={REGION_AVERAGE.symptomAvg}
                    />
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2, backgroundColor: colorPalette.white }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: colorPalette.textPrimary, fontSize: 13 }}>
                      감정 점수 평균
                    </Typography>
                    <TrendLineChart data={emoTrend} color="#f59e0b" maxY={emoMaxY} />
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2, backgroundColor: colorPalette.white }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: colorPalette.textPrimary, fontSize: 13 }}>
                      디스트레스 점수 평균
                    </Typography>
                    <TrendLineChart
                      data={distressTrend}
                      color="#8b5cf6"
                      maxY={distressMaxY}
                      regionAvg={REGION_AVERAGE.distressAvg}
                    />
                  </Paper>
                </Box>
              </>
            )}
            </>
          )}
        </TabPanel>

          {/* 탭 3: 만족도조사 통계 (월성복지관 소속 전용) */}
          {showSatisfactionTab && (
            <TabPanel value={tab} index={2}>
              <SurveyStatsView />
            </TabPanel>
          )}
        </>
    </Paper>
  );
};

export default UserDetailView;
